import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Custom Dropdown Component - Stitch design
 * Styled to match FormInput (gray background, border, same height)
 *
 * @param {Object} props
 * @param {string} props.value - Current selected value
 * @param {Function} props.onChange - Callback when value changes
 * @param {Array} props.options - Array of { value, label, dot?, icon? } objects
 * @param {string} props.className - Additional classes
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether disabled
 * @param {string} props.position - Dropdown position: 'left' or 'right'
 * @param {React.Component} props.icon - Optional left icon
 * @param {string} props.error - Validation error message
 */
const CustomDropdown = ({
    value,
    onChange,
    options = [],
    className = '',
    placeholder = 'Sélectionner...',
    disabled = false,
    position = 'left',
    icon: Icon,
    error
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const currentOption = options.find(opt => opt.value === value);
    const displayLabel = currentOption?.label || placeholder;
    const hasValue = currentOption != null;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className={`w-full flex items-center justify-between gap-2 ${Icon ? 'pl-9' : 'px-3'} pr-3 py-2 bg-slate-50 border rounded-lg text-sm text-left transition-all ${
                    error ? 'border-cabaza-pink' : 'border-slate-200'
                } ${isOpen ? 'ring-2 ring-cabaza-purple/50 border-cabaza-purple' : ''
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}`}
            >
                {Icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Icon className="w-4 h-4" />
                    </div>
                )}
                <span className={`flex items-center gap-2 truncate ${hasValue ? 'text-slate-800' : 'text-slate-400'}`}>
                    {currentOption?.dot && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${currentOption.dot}`} />}
                    {currentOption?.icon && <currentOption.icon className="w-4 h-4 flex-shrink-0" />}
                    {displayLabel}
                </span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div role="listbox" className={`absolute top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-full max-h-60 overflow-y-auto ${position === 'right' ? 'right-0' : 'left-0'}`}>
                        {options.map((option) => {
                            const isSelected = option.value === value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => { onChange(option.value); setIsOpen(false); }}
                                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between gap-2 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg ${isSelected ? 'text-cabaza-purple font-medium bg-cabaza-purple/5' : 'text-slate-700'}`}
                                >
                                    <span className="flex items-center gap-2 truncate">
                                        {option.dot && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${option.dot}`} />}
                                        {option.icon && <option.icon className="w-4 h-4 flex-shrink-0" />}
                                        {option.label}
                                    </span>
                                    {isSelected && <Check className="w-4 h-4 text-cabaza-purple flex-shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
            {error && <p className="mt-1 text-xs text-cabaza-pink">{error}</p>}
        </div>
    );
};

export default CustomDropdown;
